// The Medication Administration Record annexure appended to the finalized
// discharge summary PDF.
//
// This is a RENDER-TIME artefact and nothing else. The HTML it produces is
// concatenated onto the document at its two render surfaces (the Step-3 preview
// and generatePdfBlobFromHtml) and never enters `htmlContent`, `summaryData`,
// `final_json`, save_discharge_summary or the validate_discharge_payload
// articulation payload — the same containment carePlanMedNamesRef uses. A
// nurse-signed dose record must not become doctor-editable prose, and the LLM
// pass must not be able to rewrite it.
//
// The existing treatment_given.medications_administered table is untouched by
// this module. It is populated from care plan ORDERS; this is the record of
// what was actually given. The two can disagree, and that is intended.
import axios from "axios";
import {
  isMedicationTask,
  medicationOf,
  doseRouteLabel,
  outcomeOf,
} from "../../nurse-dashboard/medicationDisplay";
import {
  istDayKey,
  formatIstTime,
  wallClockDayKey,
  formatWallClockTime,
} from "../../../utils/istDateTime";

// Only these two are a record of anything. A pending or in-progress dose is
// work outstanding, not a fact about the patient. `cancelled` never arrives —
// nurse_dashboard_tasks/ excludes it server-side when no status filter is sent.
const RECORDED_STATUSES = new Set(["completed", "missed"]);

/**
 * The IST calendar day and clock time a dose row is filed under.
 *
 * The two timestamps are NOT interchangeable (see src/utils/istDateTime.js:22-27):
 * `completed_at` is a real instant and must be converted to IST, while
 * `scheduled_datetime` holds the wall-clock time a human picked — the "08:00
 * round" is 08:00 on the ward clock — and must be read from its own digits.
 * A completed dose is stamped with the former; a missed one only ever has the
 * latter. Reading scheduled_datetime as an instant shifts it by 5h30m.
 */
export function rowWhen(task) {
  // The nurse's own recorded administration time wins: it is the clinical fact,
  // while completed_at is merely when the record was saved. They agree when the
  // dose is recorded as it is given, and diverge on a retrospective entry.
  // administered_at is a datetime-local the nurse filled, so it is WALL CLOCK.
  const recorded = task?.data?.administered_at;
  if (recorded) {
    return {
      day: wallClockDayKey(recorded),
      time: formatWallClockTime(recorded),
    };
  }
  if (task?.completed_at) {
    return {
      day: istDayKey(task.completed_at),
      time: formatIstTime(task.completed_at),
    };
  }
  return {
    day: wallClockDayKey(task?.scheduled_datetime),
    time: formatWallClockTime(task?.scheduled_datetime),
  };
}

// Lexicographic and therefore safe to compare across the instant/wall-clock
// split, which a Date-based comparator would not be. Undated rows sort last:
// they are a data defect and burying them at the top would hide it.
const sortKey = (task) => {
  const { day, time } = rowWhen(task);
  return `${day || "9999-99-99"} ${time || "99:99"}`;
};

/** Medication doses that were actually recorded, in chronological order. */
export function selectMarRows(tasks) {
  return (Array.isArray(tasks) ? tasks : [])
    .filter(
      (t) =>
        isMedicationTask(t) &&
        RECORDED_STATUSES.has(String(t?.status || "").toLowerCase())
    )
    .sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
}

// ─── Rendering ────────────────────────────────────────────────────────────

// The fragment is injected with innerHTML / dangerouslySetInnerHTML, and drug
// names and free-text observations are user-entered.
const esc = (v) =>
  String(v == null ? "" : v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const TABLE = "width:100%;border-collapse:collapse;font-size:11px;margin:0 0 12px 0;";
const TH = "border:1px solid #333;padding:4px 6px;text-align:left;background:#f0f0f0;font-weight:600;";
const TD = "border:1px solid #333;padding:4px 6px;vertical-align:top;";

const COLUMNS = ["Time", "Medicine", "Dose / Route", "Outcome", "Administered by", "Remarks"];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// A day key is ALREADY an IST/wall-clock calendar date, so format its digits
// directly. Passing it back through a Date would apply a second timezone
// conversion to a value that has had one, rolling the day on machines east of
// IST.
const formatDayKey = (key) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key || "");
  return m ? `${m[3]} ${MONTHS[Number(m[2]) - 1]} ${m[1]}` : "Date not recorded";
};

const cells = (task, time) => [
  time || "—",
  medicationOf(task).name || "—",
  doseRouteLabel(task),
  outcomeOf(task).label,
  task?.performed_by_name || "—",
  task?.data?.observations || "",
];

/**
 * The MAR annexure as an HTML fragment. Returns "" when nothing was recorded —
 * an empty annexure must not print.
 *
 * One table per calendar day. That is clinically natural AND it is what keeps
 * each table short enough to sit on one page: html2pdf runs pagebreak mode
 * 'avoid-all', which cannot split a single table taller than a page.
 */
export function buildMarAnnexureHtml(tasks) {
  const rows = selectMarRows(tasks);
  if (rows.length === 0) return "";

  const byDay = new Map();
  rows.forEach((task) => {
    const { day, time } = rowWhen(task);
    if (!byDay.has(day)) byDay.set(day, []);
    byDay.get(day).push({ task, time });
  });

  const header = `<tr>${COLUMNS.map((c) => `<th style="${TH}">${esc(c)}</th>`).join("")}</tr>`;

  const sections = Array.from(byDay.entries())
    .map(([day, entries]) => {
      const body = entries
        .map(
          ({ task, time }) =>
            `<tr>${cells(task, time)
              .map((v) => `<td style="${TD}">${esc(v)}</td>`)
              .join("")}</tr>`
        )
        .join("");
      return (
        `<div class="mar-day-group" style="page-break-inside:avoid;break-inside:avoid;margin-top:12px;">` +
        `<p style="font-weight:600;font-size:12px;margin:0 0 4px 0;page-break-after:avoid;break-after:avoid;">${esc(formatDayKey(day))}</p>` +
        `<table style="${TABLE}"><thead>${header}</thead><tbody>${body}</tbody></table>` +
        `</div>`
      );
    })
    .join("");

  return (
    `<div class="html2pdf__page-break page-break" style="page-break-before:always;break-before:page;"></div>` +
    `<div class="mar-annexure" style="page-break-before:always;break-before:page;">` +
    `<h2 style="font-size:14px;margin:12px 0 2px 0;page-break-after:avoid;break-after:avoid;">Annexure — Medication Administration Record</h2>` +
    `<p style="font-size:10px;color:#555;margin:0 0 10px 0;page-break-after:avoid;break-after:avoid;">` +
    `Every dose recorded against this admission, as entered by the administering nurse.</p>` +
    sections +
    `</div>`
  );
}

/**
 * Every dose task for an admission.
 *
 * No `status` filter is sent on purpose: the view then excludes `cancelled`
 * server-side (a stopped order is not a dose event) and returns everything
 * else, which selectMarRows narrows. `date` is optional, so one call covers the
 * whole admission.
 */
export async function fetchMarRows(admissionId, headers) {
  const base = process.env.REACT_APP_BACKEND_ROUTE_URL;
  const res = await axios.get(
    `${base}nurse_dashboard_tasks/?admission_id=${admissionId}`,
    { headers }
  );
  return Array.isArray(res?.data?.data) ? res.data.data : [];
}
